import React, { useContext, useEffect, useRef, useState } from "react";
import CaretIcon from './assets/caret_icon.svg'
import FlexBox from "schoolUiComponents/basic/Box/FlexBox";
import Box from "schoolUiComponents/basic/Box";
import { Colors } from "baseStyles/enums/colors";
import './styles.scss';
import ScrollerStyles from "./styles";
import { FlexProps } from "schoolUiComponents/basic/Box/FlexBox/types";
import PositionBox from "schoolUiComponents/basic/Box/PositionBox";
import { PositionBoxPropsType } from "schoolUiComponents/basic/Box/PositionBox/types";

interface IScrollerProps {
    onScrollChange?: (val: string) => void;
    children: any
    refreshDependency?: any;
}

type ScrollerContextValueType = {
    scrollableViewRef: any;
    scroll: (val: 1 | -1) => void;
    handleClick: (event: any) => void;
    hasNext: boolean;
    hasPrev: boolean;
}

type ScrollerComponentType = {
    Next: React.FC<{newIcon?: boolean, blueIcon?: boolean}>,
    Prev: React.FC<{newIcon?: boolean, blueIcon?: boolean}>,
    Box: React.FC<{fullWidth?: boolean, customStyles?: boolean}>
    positionedNext: React.FC<PositionBoxPropsType>,
    positionedPrev: React.FC<PositionBoxPropsType>,
}

const ScrollerContext = React.createContext({} as ScrollerContextValueType);

const Scroller: React.FC<IScrollerProps> & ScrollerComponentType = ({ onScrollChange, children, refreshDependency }) => {
    const scrollableViewRef = useRef<any>();
    const [hasNext, setHasNext] = useState(false);
    const [hasPrev, setHasPrev] = useState(false);
    const scrollTimeoutRef = useRef(null);
    const scrollLeftValue = useRef(0);

    useEffect(() => {
        checkScrollState();
    }, [refreshDependency])

    const scroll = (val: 1 | -1) => {
        try {
            if (hasNext || hasPrev) {
                const width = scrollableViewRef.current?.clientWidth - 50;
                scrollableViewRef.current?.scrollBy({
                    left: width * val,
                    behavior: 'smooth'
                });
            }
        } catch (e) {
            console.error(e);
        }
    }

    const checkScrollState = (event?: any) => {
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = null;
        }
        scrollTimeoutRef.current = setTimeout(() => {
            try {
                const scrollLeft = scrollableViewRef.current?.scrollLeft;
                const scrollWidth = scrollableViewRef.current?.scrollWidth;
                const width = scrollableViewRef.current?.clientWidth;
                setHasPrev(scrollLeft > 0);
                setHasNext(Math.ceil(scrollLeft + width) < scrollWidth);
                if (event?.target) {
                    onScrollChange?.((event?.target?.scrollLeft > scrollLeftValue.current) ? 'Right scroll' : 'Left scroll');
                }
                scrollLeftValue.current = scrollLeft;
            } catch (e) {
                console.log(e);
            }
            clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = null;
        }, 100);
    }

    const handleClick = (event: any) => {
        try {
            // event.target?.scrollIntoView?.({
            //     behavior: 'smooth',
            //     block: 'nearest',
            //     inline: 'center'
            // });
            checkScrollState()
        } catch (e) {

        }
    }

    const scrollerContextValue: ScrollerContextValueType = {
        scrollableViewRef,
        scroll,
        handleClick,
        hasNext,
        hasPrev
    }

    useEffect(() => {
        checkScrollState();
        scrollableViewRef.current?.addEventListener('scroll', checkScrollState);
        window?.addEventListener('resize', checkScrollState);

        return () => {
            scrollableViewRef.current?.removeEventListener('scroll', checkScrollState);
            window?.removeEventListener('resize', checkScrollState);
        };
    }, [scrollableViewRef.current]);


    return (
        <ScrollerContext.Provider value={scrollerContextValue}>
            {children}
        </ScrollerContext.Provider>
    )
}

Scroller.Box = ({ children, fullWidth, customStyles }) => {
    const { scrollableViewRef, handleClick } = useContext(ScrollerContext);

    return (
        <div className={`sui-scroller${customStyles ? ' custom-styles' : ''}`} onClick={handleClick} ref={scrollableViewRef}>
            <FlexBox noWrap fullWidth={fullWidth}>{children}</FlexBox>
        </div>
    )
}

Scroller.Next = ({newIcon, blueIcon}) => {
    const { scroll, hasNext } = useContext(ScrollerContext);
    const styles = ScrollerStyles();
    const scrollButtonStyles = newIcon ? styles.newScrollButtonNext : styles.scrollButton;
    let iconToRender = (<FlexBox timeout={500} {...scrollButtonStyles} backgroundColor={hasNext ? ((newIcon || blueIcon) ? Colors.LIGHT_PURPLE_1 : Colors.MEDIUM_PURPLE) : Colors.LIGHT_GREY_1} hover={hasNext ? {backgroundColor: ((newIcon || blueIcon) ? Colors.MEDIUM_PURPLE : Colors.NAVY_BLUE_1)} : {}} transform="rotate(-90deg)" onClick={hasNext ? () => {scroll(1)} : null}><CaretIcon fill={(newIcon || blueIcon) ? Colors.NAVY_BLUE : Colors.WHITE}/></FlexBox>);
    if(newIcon){
        iconToRender = (
            <FlexBox height={88} width={107} justifyContent={FlexProps.JustifyContent.FLEX_END} background={`linear-gradient(270deg, #F7F7FA 0%, rgba(247, 247, 250, 0.70) 37.07%, rgba(247, 247, 250, 0.00) 100%)`}>
                {iconToRender}
            </FlexBox>
        )
    }
    return newIcon ? (hasNext ? iconToRender : null) : iconToRender;
}


Scroller.Prev = ({newIcon, blueIcon}) => {
    const { scroll, hasPrev } = useContext(ScrollerContext);
    const styles = ScrollerStyles();
    const scrollButtonStyles = newIcon ? styles.newScrollButtonPrev : styles.scrollButton;
    let iconToRender = (<FlexBox timeout={500} {...scrollButtonStyles} backgroundColor={hasPrev ? ((newIcon || blueIcon) ? Colors.LIGHT_PURPLE_1 : Colors.MEDIUM_PURPLE) : Colors.LIGHT_GREY_1} hover={hasPrev ? {backgroundColor: ((newIcon || blueIcon) ? Colors.MEDIUM_PURPLE : Colors.NAVY_BLUE_1)} : {}} transform="rotate(90deg)"  onClick={hasPrev ? () => {scroll(-1)} : null}><CaretIcon fill={(newIcon || blueIcon) ? Colors.NAVY_BLUE : Colors.WHITE}/></FlexBox>);
    if(newIcon){
        iconToRender = (
            <FlexBox height={88} width={107} background={`linear-gradient(270deg, rgba(247, 247, 250, 0.00) 0%, rgba(247, 247, 250, 0.70) 62.93%, #F7F7FA 100%)`}>
                {iconToRender}
            </FlexBox>
        )
    }
    return newIcon ? (hasPrev ? iconToRender : null) : iconToRender;
}

Scroller.positionedNext = ({ children, ...rest }) => {
    const { scroll, hasNext } = useContext(ScrollerContext);
    const styles = ScrollerStyles();

    if(!hasNext) {
        return null;
    }

    return (
        <PositionBox.absolute {...rest} onClick={() => { scroll(1) }}>{children}</PositionBox.absolute>
    )
}


Scroller.positionedPrev = ({ children, ...rest }) => {
    const { scroll, hasPrev } = useContext(ScrollerContext);
    const styles = ScrollerStyles();

    if(!hasPrev) {
        return null;
    }

    return (
        <PositionBox.absolute {...rest} onClick={() => { scroll(-1) }}>{children}</PositionBox.absolute>
    )
}

export default Scroller;